import React from 'react';
import { Box, Chip, CircularProgress } from '@mui/material';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import {
    type ProjectSubmission,
    type SaoAttachmentSummary,
    type SaoPerCheckStatus,
    type SaoPerCheckType,
} from '../api';
import { SAO_DISPLAY_STATUS_CONFIG, SAO_PER_CHECK_LABELS, isTerminalPositiveStatus } from './saoDisplayStatus';
import { CHIP_STYLE_OVERRIDES, GRAYED_CHIP_STYLE } from './saoCheckDetailStyles';

interface SaoCheckCellProps {
    submission: ProjectSubmission;
    onSaoCheckClick: (submissionId: string, sessionId: string, checkType?: SaoPerCheckType) => void;
}

export function SaoCheckCell({ submission, onSaoCheckClick }: SaoCheckCellProps) {
    const attachments = submission.saoCheckSummary;
    if (!attachments || attachments.length === 0) return <span style={{ color: '#999' }}>—</span>;

    const isGrayed = isTerminalPositiveStatus(submission.similarityAndOriginalityStatus);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {attachments.map(attachment => (
                <AttachmentStatusRow
                    key={attachment.attachmentId}
                    attachment={attachment}
                    submissionId={submission.id}
                    isGrayed={isGrayed}
                    onSaoCheckClick={onSaoCheckClick}
                />
            ))}
        </Box>
    );
}

function AttachmentStatusRow({
    attachment,
    submissionId,
    isGrayed,
    onSaoCheckClick,
}: {
    attachment: SaoAttachmentSummary;
    submissionId: string;
    isGrayed: boolean;
    onSaoCheckClick: (submissionId: string, sessionId: string, checkType?: SaoPerCheckType) => void;
}) {
    const status = attachment.saoDisplayStatus;
    const config = SAO_DISPLAY_STATUS_CONFIG[status];

    if (status === 'processing') {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CircularProgress size={14} />
                <span style={{ fontSize: '0.75rem', color: '#666' }}>Processing</span>
            </Box>
        );
    }

    if (status === 'not_started' || status === 'not_applicable') {
        return <span style={{ color: '#999', fontSize: '0.75rem' }}>{config.label}</span>;
    }

    if (status === 'major_issues' || status === 'minor_issues') {
        const failingChecks = (attachment.perCheckStatuses ?? []).filter(s => s.displayStatus !== 'likely_ok');
        if (failingChecks.length > 0) {
            return (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {failingChecks.map(perCheck => (
                        <PerCheckPill
                            key={perCheck.checkType}
                            perCheck={perCheck}
                            submissionId={submissionId}
                            sessionId={attachment.sessionId}
                            isGrayed={isGrayed}
                            onSaoCheckClick={onSaoCheckClick}
                        />
                    ))}
                </Box>
            );
        }
        // Fall through to single rolled-up chip if perCheckStatuses is missing or all OK
        // (defensive against stale FE/BE skew — admin sees the aggregate severity).
    }

    const handleClick = () => {
        if (attachment.sessionId) onSaoCheckClick(submissionId, attachment.sessionId);
    };

    return (
        <Chip
            icon={<OpenInNewIcon sx={{ fontSize: 14, color: 'inherit' }} />}
            label={config.label}
            size="small"
            onClick={handleClick}
            sx={{
                ...(isGrayed
                    ? GRAYED_CHIP_STYLE
                    : {
                          ...CHIP_STYLE_OVERRIDES,
                          backgroundColor: `${config.backgroundColor} !important`,
                          color: `${config.color} !important`,
                      }),
                '& .MuiChip-icon': { color: 'inherit', ml: 0.75, mr: -0.25 },
            }}
        />
    );
}

function PerCheckPill({
    perCheck,
    submissionId,
    sessionId,
    isGrayed,
    onSaoCheckClick,
}: {
    perCheck: SaoPerCheckStatus;
    submissionId: string;
    sessionId: string | null;
    isGrayed: boolean;
    onSaoCheckClick: (submissionId: string, sessionId: string, checkType?: SaoPerCheckType) => void;
}) {
    const config = SAO_DISPLAY_STATUS_CONFIG[perCheck.displayStatus];
    const label = SAO_PER_CHECK_LABELS[perCheck.checkType];

    const handleClick = () => {
        if (sessionId) onSaoCheckClick(submissionId, sessionId, perCheck.checkType);
    };

    return (
        <Chip
            icon={<OpenInNewIcon sx={{ fontSize: 14, color: 'inherit' }} />}
            label={label}
            size="small"
            onClick={handleClick}
            sx={{
                ...(isGrayed
                    ? GRAYED_CHIP_STYLE
                    : {
                          ...CHIP_STYLE_OVERRIDES,
                          backgroundColor: `${config.backgroundColor} !important`,
                          color: `${config.color} !important`,
                      }),
                '& .MuiChip-icon': { color: 'inherit', ml: 0.75, mr: -0.25 },
            }}
        />
    );
}
